import { returnRetrievalRankingDebugInfo } from "@faire/retailer-shared/search/loggedInBrandLeanProductTilesRequest";
import { buildSearchProductsRequest } from "@faire/retailer-shared/search/searchRequest";
import { PAGE_SIZE } from "@faire/retailer-shared/search/searchRequest";
import { FilterSplitStrategy } from "@faire/web-api/indigofair/data/FilterSplitStrategy";
import { useMemo } from "react";

import { getSettingShouldDisplayRetrievalAndRankingDebugInfo } from "@faire/retailer/settings/getSettingShouldDisplayRetrievalAndRankingDebugInfo";
import { useProductCacheStore } from "@faire/retailer/stores/domain/ProductCacheStore";
import { UserStore } from "@faire/retailer/stores/domain/UserStore";
import {
  useSearchPageFiltersQueryQueryKey,
  useSearchPageQueryQueryKey,
} from "@faire/retailer/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryKey";
import { useSearchPageIsFromDiscoverURL } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";
import { useSearchPageParsedSearch } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageParsedSearch";

const STALE_TIME_MS = 5 * 60 * 1000;

export const useSearchPageQueryArgs = (
  pageSize: number = PAGE_SIZE,
  pageNumber?: number
) => {
  const productsQueryKey = useSearchPageQueryQueryKey(pageNumber);
  const { queryProducts } = useProductCacheStore(["queryProducts"]);
  const parsedSearch = useSearchPageParsedSearch();
  const isFromDiscoverURL = useSearchPageIsFromDiscoverURL();

  const productsRequest = buildSearchProductsRequest({
    filterSplitStrategy: FilterSplitStrategy.PRODUCTS_ONLY,
    parsedSearch: parsedSearch,
    pageSize: pageSize,
    pageNumber: pageNumber,
    shouldRequestRetrievalRankingDebugInfo: returnRetrievalRankingDebugInfo(
      UserStore.get().user,
      getSettingShouldDisplayRetrievalAndRankingDebugInfo()
    ),
    isDiscoverV3Search: isFromDiscoverURL,
  });

  return useMemo(() => {
    return {
      productsQueryArgs: {
        key: productsQueryKey,
        fn: ({ signal }: { signal: AbortSignal }) =>
          queryProducts(productsRequest, signal),
        staleTime: STALE_TIME_MS,
      },
    };
  }, [productsQueryKey, productsRequest, queryProducts]);
};

export const useSearchPageFiltersQueryArgs = () => {
  const filtersQueryKey = useSearchPageFiltersQueryQueryKey();
  const { queryFilters } = useProductCacheStore(["queryFilters"]);
  const parsedSearch = useSearchPageParsedSearch();

  const isFromDiscoverURL = useSearchPageIsFromDiscoverURL();
  const filtersRequest = buildSearchProductsRequest({
    filterSplitStrategy: FilterSplitStrategy.AGGREGATIONS_ONLY,
    parsedSearch: parsedSearch,
    shouldRequestRetrievalRankingDebugInfo: false,
    isDiscoverV3Search: isFromDiscoverURL,
  });

  return useMemo(() => {
    return {
      filtersQueryArgs: {
        key: filtersQueryKey,
        fn: ({ signal }: { signal: AbortSignal }) =>
          queryFilters(filtersRequest, signal),
        staleTime: STALE_TIME_MS,
      },
    };
  }, [filtersQueryKey, filtersRequest, queryFilters]);
};
